import { useEffect, useState } from "react";
import { Brush, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const useArtUrl = (guideId, key, version) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true, obj;
    api.get(`/guides/${guideId}/art/${key}`, { responseType: "blob" })
      .then((r) => { if (alive) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => setUrl(null));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [guideId, key, version]);
  return url;
};

export const ArtworkCard = ({ guide, onStarted }) => {
  const has = Object.keys(guide.art || {}).length > 0;
  const generating = guide.art_status === "generating";
  const cover = useArtUrl(guide.id, "cover", `${guide.updated_at}-${has}`);

  const start = async () => {
    try {
      await api.post(`/guides/${guide.id}/artwork`);
      toast.success("Drawing the cover and 10 chapter illustrations…");
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-px bg-[#E2D9CC] border border-[#E2D9CC] mb-10" data-testid="artwork-card">
      <div className="bg-[var(--g-dark,#3B1B48)] h-48 grid place-items-center p-4">
        {generating ? <Loader2 className="w-8 h-8 text-[var(--g-accent,#C59B27)] animate-spin" />
          : has && cover ? <img data-testid="artwork-cover-preview" src={cover} alt="Cover illustration" className="max-h-full max-w-full object-contain" />
          : <Brush className="w-8 h-8 text-[var(--g-accent,#C59B27)] opacity-60" strokeWidth={1.2} />}
      </div>
      <div className="bg-[#FAF7F2] p-7">
        <p className="eyebrow mb-2">Illustrations</p>
        <p data-testid="artwork-status" className="text-sm text-[#1C181D] mb-4">
          {generating ? "Drawing the cover and chapter illustrations — about a minute. The PDF and toolkit are re-typeset when done."
            : has ? `${Object.keys(guide.art).length} gold line illustrations: the cover plus one for each chapter opener.`
            : "No illustrations yet. Add a gold line-art cover and an illustration for each chapter opener."}
        </p>
        {guide.art_status === "failed" && <p data-testid="artwork-error" className="text-xs text-red-700 mb-3">{guide.art_error}</p>}
        <Button data-testid="artwork-button" variant={has ? "outline" : "default"} onClick={start} disabled={generating} className="rounded-none">
          <Brush className="w-4 h-4 mr-2" />{generating ? "Drawing…" : has ? "Redraw illustrations" : "Add illustrations"}
        </Button>
      </div>
    </div>
  );
};
